import type { BranchKey } from '@festina-life/api'
import { assertNotEmpty } from '@festina-life/core'
import { FormikForm, type FormikProps } from '@festina-life/ui/formikForms'
import { Wizard } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { getAllowedBranchCategoriesForBaseBranchCategory } from './helpers'
import type { BranchCreationInput } from './types'
import { useBranchCreationValidationSchema } from './useBranchCreationValidationSchema'
import { useBranchWizardSteps } from './useBranchWizardSteps'

type Props = {
  onCompleted: (branchKey: BranchKey) => void
  initialValues?: Partial<BranchCreationInput>
}

export const BranchCreationWizard = ({
  onCompleted,
  initialValues: externalInitialValues,
}: Props) => {
  const validationSchema = useBranchCreationValidationSchema()

  const initialValues: BranchCreationInput = useMemo(
    () => ({
      branchName: '',
      baseBranch: undefined,
      importFromFile: [],
      category: getAllowedBranchCategoriesForBaseBranchCategory(undefined)[0],
      server: undefined,
      shouldDefineTypes: false,
      shouldAddData: false,
      ...externalInitialValues,
    }),
    [externalInitialValues],
  )

  const wizardSteps = useBranchWizardSteps(
    initialValues.baseBranch || initialValues.server
      ? { baseBranch: initialValues.baseBranch, server: initialValues.server }
      : undefined,
  )

  const handleWizardCompleted =
    ({ values: { branchName, server } }: FormikProps<BranchCreationInput>) =>
    () => {
      assertNotEmpty(server)

      onCompleted({
        name: branchName,
        server,
      })
    }

  return (
    <FormikForm
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {(formikProps) => {
        if (formikProps.values.branchName.includes(' ')) {
          void formikProps.setFieldValue(
            'branchName',
            formikProps.values.branchName.replace(/\s/g, '-'),
          )
        }

        return (
          <Wizard
            steps={wizardSteps}
            currentData={formikProps}
            className="h-100"
            stepsClassName="mt-2 gap-y-4"
            onCompleted={handleWizardCompleted(formikProps)}
          />
        )
      }}
    </FormikForm>
  )
}
